import React, { Fragment, MouseEvent, useState } from 'react';
import * as XLSX from 'xlsx';
import { Box, Button, MenuItem, Typography } from '@mui/material';
import { toast } from 'react-toastify';
import Loading from '../mui/Backdrop';
import OtpDialog from '../otp/OtpDialog';

interface ExcelUploadBaseProps<T> {
  /** 업로드할 데이터 타입 */
  dataType: string;
  /** API 호출 함수 */
  onUpload: (data: T[]) => Promise<{ status: number; data: any }>;
  /** OTP 다이얼로그 제목 */
  otpTitle: string;
  /** OTP 다이얼로그 설명 */
  otpDescription: string;
  /** 업로드 성공 후 페이지 새로고침 여부 (기본값: true) */
  shouldReload?: boolean;
  /** 업로드 성공 후 추가 콜백 */
  onSuccess?: () => void;
  /** 업로드 실패 후 추가 콜백 */
  onError?: (error: any) => void;
}

export default function ExcelUploadBase<T>({
  dataType,
  onUpload,
  otpTitle,
  otpDescription,
  shouldReload = true,
  onSuccess,
  onError,
}: ExcelUploadBaseProps<T>) {
  const [data, setData] = useState<T[]>([]);
  const [failed, setFailed] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOtpDialogOpen, setIsOtpDialogOpen] = useState(false);

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: 'array' });

      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];

      const jsonData = XLSX.utils.sheet_to_json<T>(worksheet, {
        header: 0,
        raw: false,
      });
      setData(jsonData);
    };

    reader.readAsArrayBuffer(file);
  };

  const handleOtpSuccess = async () => {
    setIsOtpDialogOpen(false);
    setIsLoading(true);
    try {
      const re = await onUpload(data);

      if (re.status !== 200) {
        setFailed(re.data);
        toast.error('실패');
        onError?.(re.data);
        setIsLoading(false);
        return;
      }

      if (re.data !== null) {
        setFailed(re.data);
        toast.success('실패');
        onError?.(re.data);
      } else {
        toast.success('성공');
        onSuccess?.();

        if (shouldReload) {
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        }
      }
    } catch (e) {
      console.log(e);
      onError?.(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOtpClose = () => {
    setIsOtpDialogOpen(false);
  };

  const onSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    if (data.length === 0) {
      toast.error('업로드된 파일이 없음');
      return;
    }

    // OTP 다이얼로그 열기
    setIsOtpDialogOpen(true);
  };

  return (
    <>
      <input accept=".xlsx, .xls" type="file" onChange={handleFileUpload} style={{ marginBottom: '1rem' }} />
      <Typography variant="h6">업로드된 데이터: {data.length}</Typography>

      {failed.length === 0 && data.length > 0 && (
        <>
          <Button type="submit" onClick={onSubmit}>
            업로드
          </Button>
          <Box mb={3} />
          {data.map((item: any, index: number) => (
            <Fragment key={index}>
              <MenuItem key={index}>{JSON.stringify(item)}</MenuItem>
            </Fragment>
          ))}
        </>
      )}

      {failed.length > 0 && (
        <>
          <Typography variant="body3">실패 목록: {failed.length}</Typography>
          {failed.map((item: any, index: number) => (
            <Fragment key={index}>
              <MenuItem key={index}>{JSON.stringify(item)}</MenuItem>
            </Fragment>
          ))}
        </>
      )}

      <Loading isLoading={isLoading} />

      {/* OTP 인증 다이얼로그 */}
      <OtpDialog
        open={isOtpDialogOpen}
        onClose={handleOtpClose}
        onSuccess={handleOtpSuccess}
        title={otpTitle}
        description={otpDescription}
        isLoading={isLoading}
      />
    </>
  );
}
